import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Alert, CircularProgress, List, ListItemButton,
  ListItemText, Divider, Chip, Stack, IconButton, Tooltip, Button,
  Accordion, AccordionSummary, AccordionDetails, TextField,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import CodeIcon from '@mui/icons-material/Code';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { useApiClient } from '../../config/axios';
import FtlDetailModal from './FtlDetailModal';

interface PivotListRow {
  pattern_id: string;
  pattern_key: string;
  category: string | null;
  description: string | null;
  part_count: string;
}

interface PatternPart {
  name: string | null;
  content: string;
  part_kind: string;
  part_ordinal: number;
  subsection: string;
}

interface PatternSection {
  section: string;
  items: PatternPart[];
}

interface PivotDetail {
  pattern_id: string;
  pattern_key: string;
  category: string | null;
  description: string | null;
  raw_json: any;
  canonical_text: string;
  canonical_kind: string;
  source_file: string | null;
  version: string | null;
  sections: PatternSection[];
}

interface FtlRow {
  include_path: string;
  depth: number;
  source_section: string | null;
  ordinal: number;
  byte_size: number;
  n_metrics: number;
  metrics_here: number;
}

interface PivotsPanelProps {
  viewName: string;
}

const formatBytes = (n: number): string => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const PivotsPanel: React.FC<PivotsPanelProps> = ({ viewName }) => {
  const apiClient = useApiClient();
  const [pivots, setPivots] = useState<PivotListRow[]>([]);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [detail, setDetail] = useState<PivotDetail | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [editingOrdinal, setEditingOrdinal] = useState<number | null>(null);
  const [editBuffer, setEditBuffer] = useState<string>('');
  const [saving, setSaving] = useState(false);

  const [ftls, setFtls] = useState<FtlRow[]>([]);
  const [ftlsLoading, setFtlsLoading] = useState(false);
  const [openFtl, setOpenFtl] = useState<string | null>(null);

  useEffect(() => {
    if (!viewName) return;
    setListLoading(true);
    setSelectedKey(null);
    setDetail(null);
    setError(null);
    apiClient
      .get(`/view-manager/views/${encodeURIComponent(viewName)}/pivots`)
      .then(r => setPivots(r.data.pivots || []))
      .catch(e => setError(e?.response?.data?.error || e.message || 'Failed to load pivots'))
      .finally(() => setListLoading(false));
  }, [apiClient, viewName]);

  useEffect(() => {
    if (!selectedKey) { setDetail(null); setFtls([]); return; }
    setDetailLoading(true);
    setEditingOrdinal(null);
    apiClient
      .get(`/view-manager/pivots/${encodeURIComponent(selectedKey)}`)
      .then(r => setDetail(r.data))
      .catch(e => setError(e?.response?.data?.error || e.message || 'Failed to load pivot detail'))
      .finally(() => setDetailLoading(false));
    // FTLs fetched in parallel; failure is non-fatal (the pivot may
    // simply have no <#include> directives).
    setFtlsLoading(true);
    apiClient
      .get(`/view-manager/pivots/${encodeURIComponent(selectedKey)}/ftls`)
      .then(r => setFtls(r.data.ftls || []))
      .catch(() => setFtls([]))
      .finally(() => setFtlsLoading(false));
  }, [apiClient, selectedKey]);

  const refreshDetail = useCallback(async () => {
    if (!selectedKey) return;
    const r = await apiClient.get(`/view-manager/pivots/${encodeURIComponent(selectedKey)}`);
    setDetail(r.data);
  }, [apiClient, selectedKey]);

  const startEdit = (part: PatternPart) => {
    setEditingOrdinal(part.part_ordinal);
    setEditBuffer(part.content || '');
  };

  const cancelEdit = () => {
    setEditingOrdinal(null);
    setEditBuffer('');
  };

  const saveEdit = async () => {
    if (!selectedKey || editingOrdinal == null) return;
    setSaving(true);
    setError(null);
    try {
      await apiClient.put(
        `/view-manager/pivots/${encodeURIComponent(selectedKey)}/parts/${editingOrdinal}`,
        { content: editBuffer }
      );
      await refreshDetail();
      cancelEdit();
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const [jsonOpen, setJsonOpen] = useState(false);

  return (
    <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Left: pivot list for this view */}
      <Paper elevation={0} sx={{ width: 320, borderRight: 1, borderColor: 'divider', overflow: 'auto' }}>
        <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" color="text.secondary">
            Pivots for {viewName}
          </Typography>
        </Box>
        {listLoading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={24} /></Box>
        ) : pivots.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">No pivots linked to this view.</Typography>
          </Box>
        ) : (
          <List dense disablePadding>
            {pivots.map(p => (
              <React.Fragment key={p.pattern_key}>
                <ListItemButton
                  selected={selectedKey === p.pattern_key}
                  onClick={() => setSelectedKey(p.pattern_key)}
                >
                  <ListItemText
                    primary={p.pattern_key}
                    secondary={
                      <Stack direction="row" spacing={0.5} alignItems="center" component="span">
                        <Chip size="small" label={`${p.part_count} parts`} sx={{ height: 18 }} />
                        {p.category && <Chip size="small" label={p.category} variant="outlined" sx={{ height: 18 }} />}
                      </Stack>
                    }
                  />
                </ListItemButton>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      {/* Right: detail with sections */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}

        {!selectedKey ? (
          <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body1">Pick a pivot on the left to view its parts.</Typography>
          </Box>
        ) : detailLoading || !detail ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
        ) : (
          <>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6">{detail.pattern_key}</Typography>
              {detail.category && <Chip size="small" label={detail.category} variant="outlined" />}
              {detail.version && <Chip size="small" label={`v${detail.version}`} />}
              <Box sx={{ flex: 1 }} />
              <Button size="small" startIcon={<CodeIcon />} onClick={() => setJsonOpen(true)}>
                View raw JSON
              </Button>
            </Stack>

            {detail.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{detail.description}</Typography>
            )}

            {detail.sections.map(sec => (
              <Accordion key={sec.section} defaultExpanded={false} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle2">{sec.section}</Typography>
                    <Chip size="small" label={`${sec.items.length} items`} sx={{ height: 18 }} />
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  {sec.items.map(part => {
                    const isEditing = editingOrdinal === part.part_ordinal;
                    return (
                      <Box key={part.part_ordinal} sx={{ mb: 2, border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                            {part.name || `[ordinal ${part.part_ordinal}]`}
                          </Typography>
                          <Chip size="small" label={part.part_kind} sx={{ height: 18 }} />
                          {part.subsection !== sec.section && (
                            <Chip size="small" label={part.subsection} variant="outlined" sx={{ height: 18 }} />
                          )}
                          <Box sx={{ flex: 1 }} />
                          {isEditing ? (
                            <>
                              <Tooltip title="Save"><IconButton size="small" color="primary" onClick={saveEdit} disabled={saving}><SaveIcon fontSize="small" /></IconButton></Tooltip>
                              <Tooltip title="Cancel"><IconButton size="small" onClick={cancelEdit} disabled={saving}><CancelIcon fontSize="small" /></IconButton></Tooltip>
                            </>
                          ) : (
                            <Tooltip title="Edit"><IconButton size="small" onClick={() => startEdit(part)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                          )}
                        </Stack>
                        {isEditing ? (
                          <TextField
                            fullWidth multiline minRows={4} maxRows={20}
                            value={editBuffer}
                            onChange={e => setEditBuffer(e.target.value)}
                            InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                          />
                        ) : (
                          <Box component="pre" sx={{
                            fontFamily: 'monospace', fontSize: '0.8rem',
                            bgcolor: 'grey.100', p: 1.5, borderRadius: 1,
                            overflow: 'auto', m: 0, whiteSpace: 'pre-wrap',
                          }}>
                            {part.content}
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </AccordionDetails>
              </Accordion>
            ))}

            {/* Included FTLs — sibling accordion at the bottom of the
                section stack. Populated in parallel with detail; may be
                empty for pivots with no <#include> directives. */}
            <Accordion defaultExpanded={false} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <InsertDriveFileOutlinedIcon fontSize="small" color="action" />
                  <Typography variant="subtitle2">Included FTLs</Typography>
                  <Chip
                    size="small"
                    label={ftlsLoading ? '…' : `${ftls.length} file${ftls.length === 1 ? '' : 's'}`}
                    sx={{ height: 18 }}
                  />
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                {ftlsLoading ? (
                  <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>
                ) : ftls.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    This pivot has no <code>&lt;#include&gt;</code> directives. FTL browsing
                    requires the AP-data-pipeline to have been re-run after the FTL loader
                    landed — if this pivot definitely inlines FTLs, ask ap-core to reload.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto auto', columnGap: 2, rowGap: 0.5, alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>include_path</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>depth</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textAlign: 'right' }}>bytes</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textAlign: 'right' }}>ftl metrics</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textAlign: 'right' }}>used here</Typography>
                    <Box sx={{ gridColumn: '1 / -1', borderBottom: 1, borderColor: 'divider', my: 0.5 }} />
                    {ftls.map(row => (
                      <React.Fragment key={row.include_path}>
                        <Button
                          size="small"
                          onClick={() => setOpenFtl(row.include_path)}
                          sx={{
                            justifyContent: 'flex-start',
                            fontFamily: 'monospace',
                            fontSize: '0.8rem',
                            textTransform: 'none',
                            p: 0.5,
                          }}
                        >
                          {row.include_path}
                        </Button>
                        <Chip
                          size="small"
                          label={row.depth === 0 ? 'direct' : `via ${row.depth}`}
                          variant={row.depth === 0 ? 'filled' : 'outlined'}
                          sx={{ height: 18, justifySelf: 'start' }}
                        />
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', textAlign: 'right' }}>{formatBytes(row.byte_size)}</Typography>
                        <Typography variant="body2" sx={{ textAlign: 'right', color: row.n_metrics === 0 ? 'text.disabled' : 'text.primary' }}>{row.n_metrics}</Typography>
                        <Typography variant="body2" sx={{ textAlign: 'right', color: row.metrics_here === 0 ? 'text.disabled' : 'primary.main', fontWeight: row.metrics_here > 0 ? 500 : 400 }}>{row.metrics_here}</Typography>
                      </React.Fragment>
                    ))}
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          </>
        )}
      </Box>

      <FtlDetailModal
        open={Boolean(openFtl)}
        includePath={openFtl}
        onClose={() => setOpenFtl(null)}
        onNavigateToFtl={inc => setOpenFtl(inc)}
      />

      {jsonOpen && detail && (
        <Box
          onClick={() => setJsonOpen(false)}
          sx={{
            position: 'fixed', inset: 0, bgcolor: 'rgba(0,0,0,0.5)',
            zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Paper onClick={e => e.stopPropagation()} sx={{ maxWidth: '80vw', maxHeight: '80vh', p: 2, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>{detail.pattern_key} — raw pattern</Typography>
            <Box component="pre" sx={{ fontSize: '0.75rem', overflow: 'auto', bgcolor: 'grey.100', p: 2, borderRadius: 1, flex: 1 }}>
              {JSON.stringify(detail.raw_json, null, 2)}
            </Box>
            <Box sx={{ textAlign: 'right', mt: 1 }}>
              <Button onClick={() => setJsonOpen(false)}>Close</Button>
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default PivotsPanel;
